import React, { Component, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children?: ReactNode;
  componentName?: string;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary to catch runtime errors in child components
 * Prevents the entire app from crashing when a component fails
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const { componentName, onError } = this.props;

    // Log error to console
    console.error(
      `Error in component${componentName ? ` "${componentName}"` : ''}:`,
      error,
      errorInfo
    );

    // Call optional error handler
    if (onError) {
      onError(error, errorInfo);
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided, otherwise return null (skip component)
      return this.props.fallback || null;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
